const API_URL = process.env.REACT_APP_API_URL

export async function getBrands() {
    const res = await fetch(`${API_URL}/api/brands`)
    const data = await res.json()
    return data.brands
}

export async function getFilterMap() {
    const res = await fetch(`${API_URL}/api/filter-map`)
    const data = await res.json()
    return data.filter_map
}

export async function getRecommendations(brandIds, dislikeIds = [], includeDefaultDislikes = false, userId = '') {
    const ids = brandIds.join(',')
    const dislikes = dislikeIds.join(',')
    let url = `${API_URL}/api/recommendations?brand_ids=${ids}`
    if (dislikes) url += `&dislike_ids=${dislikes}`
    if (includeDefaultDislikes && userId) {
        url += `&include_default_dislikes=true&user_id=${userId}`
    }
    const res = await fetch(url)
    const data = await res.json()
    return data.recommendations
}

export async function getRecommendationsByAttributes(axes, themes) {
    // axes: { field: 'low'|'mid'|'high' }, themes: [string]
    const res = await fetch(`${API_URL}/api/recommendations/by-attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ axes, themes })
    })
    const data = await res.json()
    return data.recommendations
}
export async function getProfiles(userId) {
    const res = await fetch(`${API_URL}/api/profiles?user_id=${userId}`)
    const data = await res.json()
    return data.profiles
}

export async function createProfile(userId, name) {
    const res = await fetch(
        `${API_URL}/api/profiles/create?user_id=${userId}&name=${encodeURIComponent(name)}`,
        { method: 'POST' }
    )
    const data = await res.json()
    if (!res.ok) throw new Error(data.detail || 'Failed to create profile')
    return data
}

export async function deleteProfile(profileId) {
    const res = await fetch(
        `${API_URL}/api/profiles/${profileId}`,
        { method: 'DELETE' }
    )
    return res.json()
}

// Profile brands
export async function getProfileBrands(profileId) {
    const res = await fetch(`${API_URL}/api/profiles/${profileId}/brands`)
    const data = await res.json()
    return data.brands
}

export async function addProfileBrand(profileId, brandId, type = 'liked') {
    const res = await fetch(
        `${API_URL}/api/profiles/${profileId}/brands/add?brand_id=${brandId}&type=${type}`,
        { method: 'POST' }
    )
    return res.json()
}

export async function removeProfileBrand(profileId, brandId) {
    const res = await fetch(
        `${API_URL}/api/profiles/${profileId}/brands/remove?brand_id=${brandId}`,
        { method: 'DELETE' }
    )
    return res.json()
}

export async function ensureUser(userId, email) {
    const res = await fetch(
        `${API_URL}/api/user/ensure?user_id=${userId}&email=${encodeURIComponent(email)}`,
        { method: 'POST' }
    )
    return res.json()
}

// ─── Sizes ───
export async function rateProfileBrand(profileId, brandId, rating) {
    const res = await fetch(
        `${API_URL}/api/profiles/${profileId}/brands/rate?brand_id=${brandId}&rating=${rating}`,
        { method: 'POST' }
    )
    return res.json()
}

export async function unrateProfileBrand(profileId, brandId) {
    const res = await fetch(
        `${API_URL}/api/profiles/${profileId}/brands/rate?brand_id=${brandId}`,
        { method: 'DELETE' }
    )
    return res.json()
}

export async function getUserSizes(userId) {
    const res = await fetch(`${API_URL}/api/sizes/user/${userId}`)
    const data = await res.json()
    return data.sizes || []
}

export async function deleteBrandSize(userId, brandId) {
    const params = new URLSearchParams({ user_id: userId, brand_id: brandId })
    const res = await fetch(`${API_URL}/api/sizes/delete?${params}`, { method: 'DELETE' })
    return res.json()
}

export async function submitBrandSize(userId, brandId, sizes) {
    const body = {
        user_id: userId,
        brand_id: brandId,
        shirt_size: sizes.shirt_size ?? null,
        outerwear_size: sizes.outerwear_size ?? null,
        bottoms_size: sizes.bottoms_size ?? null,
        pants_waist: sizes.pants_waist ?? null,
        pants_inseam: sizes.pants_inseam ?? null,
        shoe_size: sizes.shoe_size ?? null,
    }
    const res = await fetch(`${API_URL}/api/sizes/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    })
    return res.json()
}

export async function getSizeComparison(targetBrandId, knownBrandIds, category, userSize = '') {
    const params = new URLSearchParams({
        target_brand_id: targetBrandId,
        known_brand_ids: knownBrandIds.join(','),
        category,
        user_size: userSize
    })
    const res = await fetch(`${API_URL}/api/sizes/comparison?${params}`)
    const data = await res.json()
    return data
}

// ─── Listings (resale feed) ───
// brandIds, sizes, categories accept arrays for multi-select filtering
export async function getFeed(userId = '', { profileId = '', brandIds = [], sizes = [], sizeType = '', categories = [], conditions = [], q = '' } = {}) {
    const params = new URLSearchParams()
    if (userId) params.set('user_id', userId)
    if (profileId) params.set('profile_id', profileId)
    if (brandIds.length) params.set('brand_id', brandIds.join(','))
    if (sizes.length) params.set('size', sizes.join(','))
    if (sizeType) params.set('size_type', sizeType)
    if (categories.length) params.set('category', categories.join(','))
    if (conditions.length) params.set('condition', conditions.join(','))
    if (q) params.set('q', q)
    const res = await fetch(`${API_URL}/api/feed?${params}`)
    const data = await res.json()
    return data.listings || []
}

// ─── Alerts ───
export async function getAlert(userId) {
    const res = await fetch(`${API_URL}/api/alerts/${userId}`)
    const data = await res.json()
    return data.alert || null
}

export async function saveAlert(payload) {
    const res = await fetch(`${API_URL}/api/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    })
    const data = await res.json()
    return data.alert
}

export async function deleteAlert(userId) {
    const res = await fetch(`${API_URL}/api/alerts/${userId}`, { method: 'DELETE' })
    return res.json()
}

// ─── Brand suggestions ───
export async function submitBrandSuggestion({ brandName, reason, userId, userEmail }) {
    const res = await fetch(`${API_URL}/api/suggest-brand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            brand_name: brandName,
            reason: reason || '',
            user_id: userId || null,
            user_email: userEmail || null,
        })
    })
    const data = await res.json()
    return data
}
